/**
 * useClickOutside
 * Hook utilitário para detectar cliques fora de um elemento.
 * 
 * Uso:
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * useClickOutside(ref, () => setIsOpen(false), isOpen);
 * ```
 */
import { useEffect, RefObject } from 'react';

export function useClickOutside<T extends HTMLElement>(
    ref: RefObject<T>,
    handler: () => void,
    enabled: boolean = true,
    excludeRefs: RefObject<HTMLElement>[] = []
): void {
    useEffect(() => {
        if (!enabled) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            // Se clicou dentro da ref principal, ignora
            if (ref.current && ref.current.contains(target)) {
                return;
            }

            // Se clicou em alguma ref excluída, ignora
            for (const excludeRef of excludeRefs) {
                if (excludeRef.current && excludeRef.current.contains(target)) {
                    return;
                }
            }

            // Clique fora detectado
            handler();
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [ref, handler, enabled, excludeRefs]);
}

export default useClickOutside;
